import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { SchoolLayout } from "../components/ui/school/SchoolLayout";
import DashboardHeader from "../components/ui/school/school-dashboard/dashboard-header";
import { StatCardsRow } from "../components/ui/school/school-dashboard/stats-cards";
import { CareerPathChart } from "../components/ui/school/school-dashboard/career-path-chart";
import { ClassPerformanceTable } from "../components/ui/school/school-dashboard/class-performance-table";
import { Target, TrendingUp, AlertCircle, Activity, Loader2, KeyRound, Copy, Check, Building2 } from "lucide-react";
import ProtectedRoute from "../components/ProtectedRoute";
import { AuthStore } from "../hooks/stores/AuthStore";
import { getSchoolStats, getSchool } from "@/hooks/api/schoolApi";

// ─── School Code Card ──────────────────────────────────────────────────────────

function SchoolCodeCard({ school }) {
  const [copied, setCopied] = useState(null); // null | 'main' | branchId

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  if (!school?.code) return null;

  const branches = (school.branches ?? []).filter(b => b.isActive !== false);

  return (
    <div className="bg-white rounded-xl border border-indigo-100 shadow-sm p-5 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-1.5 bg-indigo-50 rounded-lg">
          <KeyRound className="w-4 h-4 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-gray-900">Student Registration Code</h2>
          <p className="text-xs text-gray-500">Share this code with students so they can register and join your school</p>
        </div>
      </div>

      {/* Main code row */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-2.5 flex-1 max-w-xs">
          <span className="text-xl font-black text-indigo-700 tracking-widest">{school.code}</span>
        </div>
        <button
          onClick={() => copy(school.code, "main")}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors"
        >
          {copied === "main"
            ? <><Check className="w-3.5 h-3.5 text-green-500" /><span className="text-green-600">Copied</span></>
            : <><Copy className="w-3.5 h-3.5" />Copy</>
          }
        </button>
        {branches.length === 0 && (
          <p className="text-xs text-gray-400">Students enter this code at sign-up to join your school.</p>
        )}
      </div>

      {/* Branch breakdown */}
      {branches.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Share per branch — same code, different campus:
          </p>
          {branches.map(branch => (
            <div key={branch.id} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
              <div className="w-7 h-7 bg-indigo-100 rounded-lg flex items-center justify-center shrink-0">
                <Building2 className="w-3.5 h-3.5 text-indigo-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{branch.name}</p>
                <p className="text-xs text-gray-400">
                  Code: <span className="font-bold text-indigo-600 tracking-wider">{school.code}</span>
                  {[branch.address?.city, branch.address?.state].filter(Boolean).length > 0
                    ? ` · ${[branch.address?.city, branch.address?.state].filter(Boolean).join(", ")}`
                    : ""}
                </p>
              </div>
              <button
                onClick={() => copy(school.code, branch.id)}
                className="shrink-0 flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-white text-gray-500 transition-colors"
              >
                {copied === branch.id
                  ? <><Check className="w-3 h-3 text-green-500" /><span className="text-green-600">Copied</span></>
                  : <><Copy className="w-3 h-3" />Copy</>
                }
              </button>
            </div>
          ))}
          <p className="text-xs text-gray-400 pt-1">
            All branches share the same code. Students select their branch during sign-up.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const SchoolDashboardPage = () => {
  const navigate = useNavigate();
  const { isLoggedIn, currentUser, _hasHydrated } = AuthStore();
  const [school, setSchool] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  useEffect(() => {
    if (_hasHydrated && (!isLoggedIn || (currentUser?.userType !== "school_admin" && currentUser?.userType !== "super_admin"))) {
      navigate("/signin");
    }
  }, [_hasHydrated, isLoggedIn, currentUser, navigate]);

  useEffect(() => {
    if (!isLoggedIn) return;
    getSchool().then(s => setSchool(s)).catch(() => {});
    setStatsLoading(true);
    getSchoolStats()
      .then(s => setStats(s))
      .catch(err => setStatsError(err?.response?.data?.error?.message ?? "Could not load stats."))
      .finally(() => setStatsLoading(false));
  }, [isLoggedIn]);

  if (!_hasHydrated || !isLoggedIn) return null;

  const total   = stats?.totalStudents  ?? 0;
  const active  = stats?.activeStudents ?? 0;
  const male    = stats?.maleStudents   ?? 0;
  const female  = stats?.femaleStudents ?? 0;

  const statCards = [
    { id: 1, label: "Total Students",  value: statsLoading ? "…" : String(total),  icon: "Users",       trend: "", color: "text-blue-500",   bgColor: "bg-blue-50"   },
    { id: 2, label: "Active Students", value: statsLoading ? "…" : String(active), icon: "CheckCircle", trend: "", color: "text-green-500",  bgColor: "bg-green-50"  },
    { id: 3, label: "Male",            value: statsLoading ? "…" : String(male),   icon: "Users",       trend: "", color: "text-purple-500", bgColor: "bg-purple-50" },
    { id: 4, label: "Female",          value: statsLoading ? "…" : String(female), icon: "Briefcase",   trend: "", color: "text-rose-500",   bgColor: "bg-rose-50"   },
  ];

  const careerPathData = [{ name: "No data yet", value: 1 }];
  const schoolName = school?.name ?? currentUser?.name ?? "School";

  return (
    <ProtectedRoute>
      <SchoolLayout>
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">

          <DashboardHeader schoolName={schoolName} />

          <SchoolCodeCard school={school} />

          <StatCardsRow stats={statCards} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Career Path Distribution */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Target className="w-4 h-4 text-blue-600" />
                      <h2 className="text-lg font-bold text-gray-900">Career Path Analysis</h2>
                    </div>
                    <p className="text-sm text-gray-500">
                      Career path data will appear as students enrol
                    </p>
                  </div>
                </div>
                <CareerPathChart data={careerPathData} />
              </div>
            </div>

            {/* Class Performance */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 h-full">
                <div className="flex items-center gap-2 mb-5">
                  <div className="p-1.5 bg-purple-100 rounded-md">
                    <TrendingUp className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Class Performance</h2>
                    <p className="text-sm text-gray-500">Goal & achievement rate</p>
                  </div>
                </div>

                <p className="text-sm text-gray-400 text-center py-6">No class data yet</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Top Career Paths */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Career Paths</h3>
              <div className="text-center py-6 space-y-2">
                <Target className="w-8 h-8 text-gray-300 mx-auto" />
                <p className="text-sm text-gray-400">No career paths selected yet</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Activity</h3>
              <div className="text-center py-6 space-y-2">
                <Activity className="w-8 h-8 text-gray-300 mx-auto" />
                <p className="text-sm text-gray-400">No activity logged yet</p>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Key Metrics</h3>
              {statsLoading ? (
                <div className="flex items-center justify-center py-8 gap-2 text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading…</span>
                </div>
              ) : statsError ? (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-red-600">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <p className="text-xs">{statsError}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {[
                    { label: "Student Engagement", value: total > 0 ? Math.round((active / total) * 100) : 0, color: "from-green-400 to-emerald-500" },
                    { label: "Active Rate", value: total > 0 ? Math.round((active / total) * 100) : 0, color: "from-blue-400 to-blue-600" },
                    { label: "Enrolment Fill", value: 0, color: "from-purple-400 to-purple-600" },
                  ].map(m => (
                    <div key={m.label}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">{m.label}</span>
                        <span className="text-sm font-bold text-gray-900">{m.value}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full bg-linear-to-r ${m.color} rounded-full transition-all duration-500`} style={{ width: `${m.value}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      </SchoolLayout>
    </ProtectedRoute>
  );
};

export default SchoolDashboardPage;
